import { useEffect, useState, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Divider,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from "@nextui-org/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DefaultLayout from "@/layouts/default";
import { dashboardAPI } from "@/services/api";

interface DashboardStats {
  totalBooks: number;
  totalInventoryValue: number;
  totalSales: number;
  topSellingBooks: Array<{
    book_name: string;
    total_quantity: number;
    total_revenue: number;
  }>;
  lowSellingBooks: Array<{
    book_name: string;
    total_quantity: number;
    total_revenue: number;
  }>;
  booksByCategory: Array<{
    category: string;
    count: number;
  }>;
  lowStockBooks: Array<{
    id: string;
    title: string;
    category: string;
    currentStock: number;
    minStock: number;
  }>;
}

export default function IndexPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalBooks: 0,
    totalInventoryValue: 0,
    totalSales: 0,
    topSellingBooks: [],
    lowSellingBooks: [],
    booksByCategory: [],
    lowStockBooks: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  // Use refs to prevent multiple calls during component lifecycle
  const hasFetchedData = useRef<boolean>(false);

  useEffect(() => {
    // Only fetch if we haven't already fetched data
    if (hasFetchedData.current) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch dashboard statistics
        const dashboardStats = await dashboardAPI.getStats();

        // Mark that we've fetched data
        hasFetchedData.current = true;

        // Check for any naming inconsistencies in the API response
        const apiKeys = Object.keys(dashboardStats);

        // Try to detect if data exists under different property names
        const possibleTopSellingKeys = apiKeys.filter(key => 
          key.toLowerCase().includes('top') || 
          key.toLowerCase().includes('selling') || 
          key.toLowerCase().includes('sales')
        );
        
        const possibleLowStockKeys = apiKeys.filter(key => 
          key.toLowerCase().includes('low') || 
          key.toLowerCase().includes('stock') || 
          key.toLowerCase().includes('inventory')
        );

        // Create a processed data object, handling possible naming differences
        const processedData: Partial<DashboardStats> = {
          totalBooks: dashboardStats.totalBooks || 0,
          totalInventoryValue: dashboardStats.totalInventoryValue || 0,
          totalSales: dashboardStats.totalSales || 0,
        };

        // Process books by category
        if (dashboardStats.booksByCategory && Array.isArray(dashboardStats.booksByCategory)) {
          processedData.booksByCategory = dashboardStats.booksByCategory;
        } else {
          processedData.booksByCategory = [];
        }

        // Try to find top selling books data
        if (dashboardStats.topSellingBooks) {
          processedData.topSellingBooks = dashboardStats.topSellingBooks;
        } else if (possibleTopSellingKeys.length > 0) {
          // Try first potential match
          const key = possibleTopSellingKeys[0];
          if (Array.isArray(dashboardStats[key])) {
            processedData.topSellingBooks = dashboardStats[key];
          }
        }

        // Process low selling books
        if (dashboardStats.lowSellingBooks && Array.isArray(dashboardStats.lowSellingBooks)) {
          processedData.lowSellingBooks = dashboardStats.lowSellingBooks;
        } else {
          processedData.lowSellingBooks = [];
        }

        // Try to find low stock books data - use API response directly if available
        if (dashboardStats.lowStockBooks && Array.isArray(dashboardStats.lowStockBooks)) {
          processedData.lowStockBooks = dashboardStats.lowStockBooks;
        } else if (possibleLowStockKeys.length > 0) {
          // Try first potential match
          const key = possibleLowStockKeys[0];
          if (Array.isArray(dashboardStats[key])) {
            processedData.lowStockBooks = dashboardStats[key];
          }
        } else if (dashboardStats.books) {
          // Look for books array with stock information if lowStockBooks isn't found
          // Filter books that have stock at or below min_stock
          const lowStock = dashboardStats.books.filter((book: any) => 
            book.quantity <= (book.min_stock || 0) + 5
          ).map((book: any) => ({
            id: book.id || book._id,
            title: book.title || book.name || book.book_name,
            category: book.category || 'Uncategorized',
            currentStock: book.quantity || book.stock || 0,
            minStock: book.min_stock || book.minStock || 0
          }));
          
          if (lowStock.length > 0) {
            processedData.lowStockBooks = lowStock;
          }
        }

        // Ensure we have arrays even if no data
        if (!processedData.lowStockBooks) {
          processedData.lowStockBooks = [];
        }
        if (!processedData.topSellingBooks) {
          processedData.topSellingBooks = [];
        }

        // Ensure we have a complete stats object even if API returns partial data
        setStats((prevStats) => ({
          ...prevStats,
          ...processedData,
        }));
      } catch (error: any) {
        setError(
          `Failed to load dashboard data: ${error.message || "Unknown error"}`,
        );
        // Don't reset the stats on error - keep any default values
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      // Reset the flag when component unmounts
      hasFetchedData.current = false;
    };
  }, []); // Empty dependency array - only run once

  // Helper function to format currency values
  const formatCurrency = (value: any) => {
    // Convert to number and handle null/undefined values
    const numValue = Number(value) || 0;

    return `Rs${numValue.toFixed(2)}`;
  };

  // Safe access helper to avoid "cannot read property of undefined" errors
  const getSafeValue = (obj: any, path: string[], defaultValue: any = 0) => {
    let current = obj;

    for (const key of path) {
      if (current === undefined || current === null) {
        return defaultValue;
      }
      current = current[key];
    }

    return current === undefined || current === null ? defaultValue : current;
  };

  return (
    <DefaultLayout>
      <section className="container px-8 py-2">
        <h1 className="text-3xl font-bold text-center mb-6">
          {t("dashboardTitle")}
        </h1>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <p className="text-lg">{t("loading")}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <p className="font-bold">{t("error")}</p>
            <p>{error}</p>
          </div>
        )}

        {/* Main Content - Only show when not loading and no error */}
        {!loading && !error && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <Link className="flex-1" to="/bookList">
                <Card className="cursor-pointer h-full">
                  <CardHeader className="flex gap-3">
                    <div className="flex flex-col">
                      <p className="text-md">{t("totalBooks")}</p>
                      <p className="text-2xl font-bold">{stats.totalBooks}</p>
                    </div>
                  </CardHeader>
                  <Divider />
                  <CardBody>
                    <p>{t("totalBooksDescription")}</p>
                  </CardBody>
                </Card>
              </Link>

              <Link className="flex-1" to="/inventory">
                <Card className="cursor-pointer h-full">
                  <CardHeader className="flex gap-3">
                    <div className="flex flex-col">
                      <p className="text-md">{t("totalInventoryPrice")}</p>
                      <p className="text-2xl font-bold">
                        {formatCurrency(stats.totalInventoryValue)}
                      </p>
                    </div>
                  </CardHeader>
                  <Divider />
                  <CardBody>
                    <p>{t("totalInventoryPriceDescription")}</p>
                  </CardBody>
                </Card>
              </Link>

              <Link className="flex-1" to="/sales">
                <Card className="cursor-pointer h-full">
                  <CardHeader className="flex gap-3">
                    <div className="flex flex-col">
                      <p className="text-md">{t("totalSales")}</p>
                      <p className="text-2xl font-bold">
                        {formatCurrency(stats.totalSales)}
                      </p>
                    </div>
                  </CardHeader>
                  <Divider />
                  <CardBody>
                    <p>{t("totalSalesDescription")}</p>
                  </CardBody>
                </Card>
              </Link>
            </div>

            {/* Books by Category */}
            <div className="mb-8">
              <Card>
                <CardHeader>
                  <h2 className="text-xl font-bold">
                    {t("booksByCategory", "Books by Category")}
                  </h2>
                </CardHeader>
                <Divider />
                <CardBody>
                  {getSafeValue(stats, ["booksByCategory"], []).length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {getSafeValue(stats, ["booksByCategory"], []).map(
                        (
                          category: {
                            category: string;
                            count: number;
                          },
                          index: number,
                        ) => (
                          <Card key={index} className="bg-gradient-to-br from-blue-50 to-indigo-100">
                            <CardBody className="text-center py-4">
                              <p className="text-sm text-default-600 mb-1">
                                {category.category}
                              </p>
                              <p className="text-2xl font-bold text-primary">
                                {category.count}
                              </p>
                              <p className="text-xs text-default-500">
                                {t("books", "books")}
                              </p>
                            </CardBody>
                          </Card>
                        ),
                      )}
                    </div>
                  ) : (
                    <p className="text-center py-4">
                      {t("noCategoriesData", "No category data available")}
                    </p>
                  )}
                </CardBody>
              </Card>
            </div>

            {/* Sales Analytics - Top and Low Selling Books */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Top Selling Books */}
              <Card>
                <CardHeader>
                  <h2 className="text-xl font-bold">
                    {t("topSellingBooks", "Top Selling Books")}
                  </h2>
                </CardHeader>
                <Divider />
                <CardBody>
                  {getSafeValue(stats, ["topSellingBooks"], []).length > 0 ? (
                    <Table aria-label="Top selling books">
                      <TableHeader>
                        <TableColumn>{t("book", "Book")}</TableColumn>
                        <TableColumn>{t("quantity", "Quantity")}</TableColumn>
                        <TableColumn>{t("revenue", "Revenue")}</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {getSafeValue(stats, ["topSellingBooks"], []).map(
                          (
                            book: {
                              book_name: string;
                              total_quantity: number;
                              total_revenue: number;
                            },
                            index: number,
                          ) => (
                            <TableRow key={index}>
                              <TableCell>{book.book_name}</TableCell>
                              <TableCell>
                                <Chip color="success" variant="flat">
                                  {book.total_quantity}
                                </Chip>
                              </TableCell>
                              <TableCell>
                                {formatCurrency(book.total_revenue)}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-4">
                      {t("noSalesData", "No sales data available")}
                    </p>
                  )}
                </CardBody>
              </Card>

              {/* Low Selling Books */}
              <Card>
                <CardHeader>
                  <h2 className="text-xl font-bold">
                    {t("lowSellingBooks", "Low Selling Books")}
                  </h2>
                </CardHeader>
                <Divider />
                <CardBody>
                  {getSafeValue(stats, ["lowSellingBooks"], []).length > 0 ? (
                    <Table aria-label="Low selling books">
                      <TableHeader>
                        <TableColumn>{t("book", "Book")}</TableColumn>
                        <TableColumn>{t("quantity", "Quantity")}</TableColumn>
                        <TableColumn>{t("revenue", "Revenue")}</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {getSafeValue(stats, ["lowSellingBooks"], []).map(
                          (
                            book: {
                              book_name: string;
                              total_quantity: number;
                              total_revenue: number;
                            },
                            index: number,
                          ) => (
                            <TableRow key={index}>
                              <TableCell>{book.book_name}</TableCell>
                              <TableCell>
                                <Chip 
                                  color={book.total_quantity === 0 ? "danger" : "warning"} 
                                  variant="flat"
                                >
                                  {book.total_quantity}
                                </Chip>
                              </TableCell>
                              <TableCell>
                                {formatCurrency(book.total_revenue)}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-4">
                      {t("noLowSalesData", "All books have good sales")}
                    </p>
                  )}
                </CardBody>
              </Card>
            </div>

            {/* Low Stock Books */}
            <div className="mb-8">
              <Card>
                <CardHeader>
                  <h2 className="text-xl font-bold">
                    {t("lowStockBooks", "Low Stock Books")}
                  </h2>
                </CardHeader>
                <Divider />
                <CardBody>
                  {getSafeValue(stats, ["lowStockBooks"], []).length > 0 ? (
                    <Table aria-label="Low stock books">
                      <TableHeader>
                        <TableColumn>{t("book", "Book")}</TableColumn>
                        <TableColumn>{t("currentStock", "Current Stock")}</TableColumn>
                        <TableColumn>{t("minStock", "Min Stock")}</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {getSafeValue(stats, ["lowStockBooks"], []).map(
                          (
                            book: {
                              id: string;
                              title: string;
                              currentStock: number;
                              minStock: number;
                            }
                          ) => (
                            <TableRow key={book.id}>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-medium">{book.title}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="font-semibold text-red-600">
                                  {book.currentStock}
                                </span>
                              </TableCell>
                              <TableCell>{book.minStock}</TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-4">
                      {t("noLowStockData", "All books have adequate stock")}
                    </p>
                  )}
                </CardBody>
              </Card>
            </div>
          </>
        )}
      </section>
    </DefaultLayout>
  );
}
